import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { Button, Col, Row, FormGroup, Label, Input } from "reactstrap";
import { DocumentTypeContext } from "../../context/DocumentContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { SignaturePlaceholder } from "../../context/hooks/useDocument";

type TemplateReplacerProps = {
  template: string;
  nameTemplate: string;
  openUseTemplate: { open: boolean; id: string };
  templateId?: string;
  isDocx?: boolean;
};

const extractTagsFromTemplate = (html: string): string[] => {
  const regex = /{{(.*?)}}/g;
  const matches = html.match(regex);
  const cleanTags =
    matches?.map((tag) => tag.replace(/[{}]/g, "").trim()) || [];
  return Array.from(new Set(cleanTags)).filter(
    (tag) => !/^ASSINATURA\d+$/i.test(tag)
  );
};

const parseDocxVariablesText = (raw: string): Record<string, string> => {
  const result: Record<string, string> = {};
  raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const eq = line.indexOf("=");
      if (eq <= 0) return;
      const key = line.slice(0, eq).trim();
      const value = line.slice(eq + 1).trim();
      if (key) result[key] = value;
    });
  return result;
};

interface GenerateTemplateResponse {
  pdfBase64: string;
  placeholders: SignaturePlaceholder[];
  requiredSignersCount: number;
}

export default function TemplateReplacer({
  template,
  nameTemplate,
  openUseTemplate,
  templateId,
  isDocx = false,
}: TemplateReplacerProps) {
  const {
    setDocument,
    setNameDocument,
    setTemplatePlaceholders,
    setTemplateRequiredSignersCount,
  } = useContext(DocumentTypeContext);

  const [tags, setTags] = useState<string[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [docxVariablesText, setDocxVariablesText] = useState("");
  const navigate = useNavigate();
  const showToast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isDocx) {
      setTags([]);
      setFormData({});
      setPreviewHtml("");
      setDocxVariablesText("");
      return;
    }
    const foundTags = extractTagsFromTemplate(template);
    setTags(foundTags);

    const initialData: Record<string, string> = {};
    foundTags.forEach((tag) => {
      initialData[tag] = "";
    });

    setFormData(initialData);
    setPreviewHtml(template);
  }, [template, isDocx]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    let updatedHtml = template;
    Object.entries(updatedFormData).forEach(([key, val]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
      updatedHtml = updatedHtml.replace(regex, val || "");
    });
    setPreviewHtml(updatedHtml);
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const variables = isDocx
        ? parseDocxVariablesText(docxVariablesText)
        : formData;

      const body: Record<string, unknown> = isDocx
        ? { templateId, variables }
        : templateId
        ? { templateId, variables }
        : { htmlContent: previewHtml };

      const response = await axios.post<GenerateTemplateResponse>(
        `${process.env.REACT_APP_API_URL}/templates/generate-pdf`,
        body,
        {
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("token")?.replace(/"/g, "") || ""
            }`,
          },
        }
      );

      const { pdfBase64, placeholders, requiredSignersCount } = response.data;
      const dataUrl = `data:application/pdf;base64,${pdfBase64}`;

      setDocument(dataUrl);
      setNameDocument(nameTemplate);
      setTemplatePlaceholders(
        placeholders && placeholders.length > 0 ? placeholders : null
      );
      setTemplateRequiredSignersCount(requiredSignersCount ?? 0);

      navigate("/admin/add-signatories");
    } catch (error: any) {
      console.log("Error generating PDF", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        "Erro ao gerar PDF";
      showToast("error", message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h4 className="mb-4">
        Preenchimento de Modelo: <strong>{nameTemplate}</strong>
      </h4>

      <Row>
        <Col md={isDocx ? "12" : "5"}>
          <div
            style={{
              background: "#fff",
              padding: "20px",
              border: "1px solid #eee",
              borderRadius: "8px",
            }}
          >
            {isDocx ? (
              <>
                <h6 className="mb-3 text-secondary">
                  Modelo DOCX
                </h6>
                <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                  As posições de assinatura serão localizadas automaticamente a
                  partir das tags <code>{"{{ASSINATURA1}}"}</code>,{" "}
                  <code>{"{{ASSINATURA2}}"}</code>... presentes no modelo.
                  Você precisará adicionar exatamente o mesmo número de
                  signatários na próxima etapa.
                </p>

                <FormGroup>
                  <Label for="docxVariables">
                    Variáveis adicionais (opcional)
                  </Label>
                  <Input
                    type="textarea"
                    id="docxVariables"
                    rows={5}
                    placeholder={"Uma por linha, no formato chave=valor\nEx.:\nNOME=João da Silva\nCPF=000.000.000-00"}
                    value={docxVariablesText}
                    onChange={(e) => setDocxVariablesText(e.target.value)}
                  />
                  <small className="text-muted">
                    Use este campo apenas se o seu modelo tiver variáveis como
                    {" "}<code>{"{{NOME}}"}</code> ou{" "}
                    <code>{"{{CPF}}"}</code>. As tags{" "}
                    <code>{"{{ASSINATURAn}}"}</code> são tratadas separadamente
                    e não devem ser informadas aqui.
                  </small>
                </FormGroup>
              </>
            ) : (
              <>
                <h6 className="mb-3 text-secondary">Preencha os campos</h6>
                {tags.map((tag) => (
                  <FormGroup key={tag}>
                    <Label for={tag}>{tag}</Label>
                    <Input
                      type="text"
                      name={tag}
                      id={tag}
                      value={formData[tag] || ""}
                      onChange={handleChange}
                      placeholder={`Digite o valor para ${tag}`}
                    />
                  </FormGroup>
                ))}
              </>
            )}

            <Button
              color="primary"
              onClick={handleSubmit}
              block
              disabled={isLoading}
            >
              {isLoading ? "Gerando..." : "Gerar e prosseguir"}
            </Button>
          </div>
        </Col>

        {!isDocx && (
          <Col
            md="7"
            style={{
              background: "#f9f9f9",
              border: "1px solid #ddd",
              padding: "20px",
              borderRadius: "8px",
              overflowY: "auto",
            }}
          >
            <h6 className="mb-3 text-secondary">Visualização do Documento</h6>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </Col>
        )}
      </Row>
    </div>
  );
}
